import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  EditableToken,
  useContentToken,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { btnPrimary, hasValue, linkTarget } from '../utils/content';

const bgDef: SlotGroupDefinition = {
  label: 'Banner Photo',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[13].image' },
  },
  generator: {
    prompt: 'The best wide exterior or aerial photo of the building and its surroundings.',
    context: 'Full-bleed banner behind a short headline and two buttons near the bottom of the home page. Text sits on the right half.',
  },
};

const primaryCtaDef: SlotGroupDefinition = {
  label: 'Button 1',
  description: 'Hidden while the URL is empty.',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
    vibesite_event: { label: 'Tracked Event', type: 'event', defaultValue: 'schedule_tour_clicked' },
  },
};

const secondaryCtaDef: SlotGroupDefinition = {
  label: 'Button 2',
  description: 'Hidden while the URL is empty.',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/neighborhood' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'Explore the Neighborhood' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

export function ImageBanner() {
  const heading = useContentToken('imagebanner.heading');
  const bg = useSlotGroup('imagebanner.background', bgDef);
  const one = useSlotGroup('imagebanner.cta_1', primaryCtaDef);
  const two = useSlotGroup('imagebanner.cta_2', secondaryCtaDef);

  return (
    <section className="relative isolate flex min-h-[420px] items-end overflow-hidden bg-secondary md:min-h-[540px] lg:min-h-[600px]">
      <EditableSlotGroup groupPath="imagebanner.background" definition={bgDef}>
        <div className="js-parallax absolute inset-0 -z-10">
          <VibesitesImage url={bg.image || ''} alt="" sizes="100vw" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent md:bg-gradient-to-l md:from-black/45 md:via-black/10" />
        </div>
      </EditableSlotGroup>

      <div className="mx-auto w-full max-w-[1280px] px-6 pb-12 text-center text-white md:pb-16 md:text-right lg:px-10">
        <EditableToken
          path="imagebanner.heading"
          as="h2"
          className="js-reveal js-heading text-[28px] drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)] sm:text-[35px] lg:text-[40px]"
        >
          {heading}
        </EditableToken>
        <div className="js-stagger js-d2 mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center md:justify-end md:gap-6">
          <EditableSlotGroup groupPath="imagebanner.cta_1" definition={primaryCtaDef}>
            {hasValue(one.url) ? (
              <a
                href={one.url || '#'}
                target={linkTarget(one.url, one._target)}
                data-vibesite-event={one.vibesite_event || undefined}
                className={`${btnPrimary} min-w-[200px]`}
              >
                {one.label}
              </a>
            ) : (
              <span />
            )}
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="imagebanner.cta_2" definition={secondaryCtaDef}>
            {hasValue(two.url) ? (
              <a href={two.url || '#'} target={linkTarget(two.url, two._target)} className={`${btnPrimary} min-w-[200px]`}>
                {two.label}
              </a>
            ) : (
              <span />
            )}
          </EditableSlotGroup>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ImageBanner',
  displayName: 'Image Banner',
  tokens: {
    'imagebanner.heading': { label: 'Heading', type: 'text', defaultValue: 'Urban Living at Its Finest' },
  },
  slotGroups: {
    'imagebanner.background': bgDef,
    'imagebanner.cta_1': primaryCtaDef,
    'imagebanner.cta_2': secondaryCtaDef,
  },
};

(ImageBanner as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ImageBanner;
